import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { SOCIAL } from "../../lib/social";

/**
 * CondensedTopStrip — single-line replacement for four previously-stacked
 * ribbons (weekend weather, festival, fisheries, announcements). Rotates
 * through whichever "chips" are active every 7s so we save ~120px of
 * vertical space above the hero. Small social icons + a bright compact
 * Sign-up pill live on the right hand side of the same line.
 */
export default function CondensedTopStrip() {
  const [chips, setChips] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const results = [];
      try {
        const { data } = await api.get("/weather/weekend-headline");
        if (data?.headline) {
          results.push({ id: "weather", emoji: data.emoji || "☀️", text: data.headline, href: "/browse?bookable_now=1" });
        }
      } catch { /* noop */ }
      try {
        const { data } = await api.get("/festivals/next-featured");
        const f = data?.festival;
        if (f?.name) {
          results.push({ id: "festival", emoji: "🎪", text: `${f.name} · ${f.dates_label || "coming up"}`, href: "/festivals" });
        }
      } catch { /* noop */ }
      try {
        const { data } = await api.get("/campsites/stats");
        if (typeof data?.total === "number") {
          results.push({ id: "stats", emoji: "🏕", text: `${data.total.toLocaleString()} UK campsites · compare & book in one tap`, href: "/browse" });
        }
      } catch { /* noop */ }
      if (alive) setChips(results);
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (chips.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % chips.length), 7000);
    return () => clearInterval(id);
  }, [chips.length]);

  const chip = chips[idx];
  const fbHandle = (SOCIAL?.facebook?.handle || "campcompassuk").replace(/^@/, "");
  const igHandle = (SOCIAL?.instagram?.handle || "campcompassuk").replace(/^@/, "");

  return (
    <div className="bg-gradient-to-r from-[#ffb703] via-[#fb8500] to-[#ff4500] text-white text-[12px] sm:text-sm">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 py-1.5 flex items-center gap-2 sm:gap-3">
        {chip && (
          <Link
            to={chip.href}
            className="flex-1 min-w-0 truncate flex items-center gap-2 hover:brightness-110 transition"
            data-testid="top-strip-chip"
          >
            <span aria-hidden className="text-base leading-none shrink-0">{chip.emoji}</span>
            <span className="truncate font-semibold">{chip.text}</span>
            <ArrowRight className="w-3 h-3 shrink-0 opacity-80" />
          </Link>
        )}
        {!chip && <span className="flex-1"></span>}

        {/* Social icons — compact, still stand-out with white circles */}
        <a
          href={`https://facebook.com/${fbHandle}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Follow us on Facebook"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/95 text-[#0e0e0e] hover:bg-white shrink-0"
          data-testid="top-strip-fb"
        >
          <Facebook className="w-3.5 h-3.5" strokeWidth={2.4} />
        </a>
        <a
          href={`https://instagram.com/${igHandle}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Follow us on Instagram"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/95 text-[#0e0e0e] hover:bg-white shrink-0"
          data-testid="top-strip-ig"
        >
          <Instagram className="w-3.5 h-3.5" strokeWidth={2.4} />
        </a>

        <Link
          to="/register"
          className="inline-flex items-center px-2.5 py-1 rounded-full bg-white text-[#ff4500] text-[10px] sm:text-[11px] font-black uppercase tracking-wider shrink-0 hover:bg-[#fff5ee]"
          data-testid="top-strip-signup"
        >
          <span className="sm:hidden">Sign up</span>
          <span className="hidden sm:inline">Sign up free</span>
        </Link>
      </div>
    </div>
  );
}
